import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Box,
	Typography,
} from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";

import DropdownController from "../../components/inputs/DropdownController";
import DatePickerController from "../../components/inputs/DatePickerController";
import TextFieldController from "../../components/inputs/TextFieldController";
import PATCloseButton from "../../components/Buttons/PATCloseButton";
import PATSaveButton from "../../components/Buttons/PATSaveButton";
import { isValidDateTest, maxLengthMessage } from "../../utils/functions";
import PATPrintButton from "../../components/Buttons/PATPrintButton";

const MAINT_STATUSES = [
	"Scheduled",
	"In Progress",
	"Completed",
	"Cancelled",
	"Pending Parts",
	"Pending Approval",
];

const maintenanceSchema = yup.object().shape({
	date: isValidDateTest("Date"),
	status: yup.string().trim().max(50, maxLengthMessage(50)).nullable(),
	statusDate: isValidDateTest("Status Date"),
	cost: yup
		.number()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),
	performedBy: yup.string().trim().max(100, maxLengthMessage(100)).nullable(),
	assignedTo: yup.string().trim().max(100, maxLengthMessage(100)).nullable(),
	equipmentID: yup
		.string()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),
	svcReqID: yup
		.string()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),
	fundingSourceID: yup
		.number()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),
	description: yup
		.string()
		.trim()
		.max(1000, maxLengthMessage(1000))
		.nullable(),
	notes: yup.string().trim().max(500, maxLengthMessage(500)).nullable(),
});

const buildDefaults = (initialData) => ({
	date: initialData?.date
		? moment(initialData.date).format("MM/DD/YYYY")
		: "",
	status: initialData?.status || "",
	statusDate: initialData?.statusDate
		? moment(initialData.statusDate).format("MM/DD/YYYY")
		: "",
	cost: initialData?.cost || "",
	performedBy: initialData?.performedBy || "",
	assignedTo: initialData?.assignedTo || "",
	equipmentID: initialData?.equipmentID
		? String(initialData.equipmentID)
		: "",
	svcReqID: initialData?.svcReqID ? String(initialData.svcReqID) : "",
	fundingSourceID: initialData?.fundingSourceID
		? String(initialData.fundingSourceID)
		: "",
	description: initialData?.description || "",
	notes: initialData?.notes || "",
});

const AddEditMaintenanceDialog = ({
	open,
	onClose,
	onSave,
	initialData,
	viewOnly = false,
	onPrint,
	// Option data
	employees = [],
	equipment = [],
	serviceRequirements = [],
	fundingSources = [],
}) => {
	const user = useSelector((state) => state.user);

	const {
		control,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(maintenanceSchema),
		defaultValues: buildDefaults(initialData),
	});

	// Reset form when dialog opens with new data
	useEffect(() => {
		reset(buildDefaults(initialData));
	}, [initialData, reset]);

	// Reset on close
	useEffect(() => {
		if (!open) {
			reset();
		}
	}, [open, reset]);

	const onSubmit = (data) => {
		const payload = {
			date: data.date
				? moment(data.date, "MM/DD/YYYY").format("YYYY-MM-DD")
				: null,
			status: data.status || null,
			statusDate: data.statusDate
				? moment(data.statusDate, "MM/DD/YYYY").format("YYYY-MM-DD")
				: null,
			cost: data.cost || null,
			performedBy: data.performedBy?.trim() || null,
			assignedTo: data.assignedTo || null,
			equipmentID: data.equipmentID || null,
			svcReqID: data.svcReqID || null,
			fundingSourceID: data.fundingSourceID || null,
			description: data.description?.trim() || null,
			notes: data.notes?.trim() || null,
			agencyId: user?.userAgency || null,
		};

		onSave(payload);
		reset();
		onClose();
	};

	// Build option arrays
	const statusOptions = MAINT_STATUSES.map((s) => ({
		value: s,
		label: s,
	}));

	const employeeOptions = employees
		.filter((o) => o.isActive)
		.map((o) => ({
			value: `${o.rank} ${o.lastName}`,
			label: `${o.badge} — ${o.rank} ${o.lastName}`,
		}));

	const equipmentOptions = equipment.map((eq) => ({
		value: String(eq.id),
		label: [
			eq.type,
			eq.brand,
			eq.model,
			"·",
			eq.serialNumber,
			eq.propertyNumber ? `(${eq.propertyNumber})` : "",
		]
			.filter(Boolean)
			.join(" "),
	}));

	const svcReqOptions = serviceRequirements
		.filter((r) => r.isActive !== false)
		.map((sr) => ({
			value: String(sr.id),
			label: `${sr.label} (every ${sr.intervalDays}d)`,
		}));

	const fundingSourceOptions = fundingSources.map((fs) => ({
		value: String(fs.id),
		label: fs.title,
	}));

	const dialogTitle = initialData
		? viewOnly
			? "View Maintenance Record"
			: "Edit Maintenance Record"
		: "Add Maintenance Record";

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
			<DialogTitle>{dialogTitle}</DialogTitle>

			<DialogContent
				sx={{
					pointerEvents: viewOnly ? "none" : undefined,
					opacity: viewOnly ? 0.75 : undefined,
				}}
			>
				<Box
					component="form"
					onSubmit={handleSubmit(onSubmit)}
					noValidate
				>
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: "repeat(3, 1fr)",
							gap: 2,
							pt: 1,
						}}
					>
						{/* Row 1 */}
						<Box>
							<DatePickerController
								name="date"
								label="Date"
								control={control}
								errors={errors}
							/>
						</Box>
						<Box>
							<DropdownController
								name="status"
								label="Status"
								options={statusOptions}
								control={control}
								errors={errors}
							/>
						</Box>
						<Box>
							<DatePickerController
								name="statusDate"
								label="Status Date"
								control={control}
								errors={errors}
							/>
						</Box>

						{/* Row 2 */}
						<Box>
							<TextFieldController
								name="cost"
								label="Cost ($)"
								control={control}
								errors={errors}
								type="number"
								placeholder="0.00"
							/>
						</Box>
						<Box>
							<TextFieldController
								name="performedBy"
								label="Performed By"
								control={control}
								errors={errors}
								placeholder="Technician, vendor, or staff name"
							/>
						</Box>
						<Box>
							<DropdownController
								name="assignedTo"
								label="Assigned To"
								options={employeeOptions}
								control={control}
								errors={errors}
								placeholder="— None —"
							/>
						</Box>

						{/* Full-width fields */}
						<Box sx={{ gridColumn: "1 / -1" }}>
							<DropdownController
								name="equipmentID"
								label="Equipment (optional)"
								options={equipmentOptions}
								control={control}
								errors={errors}
								placeholder="— Not linked to specific equipment —"
							/>
						</Box>

						<Box sx={{ gridColumn: "1 / -1" }}>
							<DropdownController
								name="svcReqID"
								label="Fulfills Service Requirement (optional)"
								options={svcReqOptions}
								control={control}
								errors={errors}
								placeholder="— Not linked to a service requirement —"
							/>
						</Box>

						<Box sx={{ gridColumn: "1 / -1" }}>
							<DropdownController
								name="fundingSourceID"
								label="Funding Source (optional)"
								options={fundingSourceOptions}
								control={control}
								errors={errors}
								placeholder="— Not linked to funding source —"
							/>
						</Box>

						<Box sx={{ gridColumn: "1 / -1" }}>
							<TextFieldController
								name="description"
								label="Description"
								control={control}
								errors={errors}
								multiline
								rows={3}
								placeholder="Describe the maintenance performed or required..."
							/>
						</Box>

						<Box sx={{ gridColumn: "1 / -1" }}>
							<TextFieldController
								name="notes"
								label="Notes"
								control={control}
								errors={errors}
								multiline
								rows={2}
								placeholder="Additional notes, parts used, follow-up actions..."
							/>
						</Box>
					</Box>
				</Box>
			</DialogContent>

			<DialogActions sx={{ px: 3, pb: 2 }}>
				{initialData && onPrint && (
					<PATPrintButton
						onClick={() => onPrint(initialData)}
						tooltipText="Print maintenance record"
						label="🖨️ Print"
						variant="outlined"
						sx={{ mr: "auto" }}
					/>
				)}
				{!viewOnly && (
					<PATSaveButton
						onClick={handleSubmit(onSubmit)}
						tooltipText="Save"
					/>
				)}
				<PATCloseButton onClick={onClose} tooltipText="Close" />
			</DialogActions>
		</Dialog>
	);
};

export default AddEditMaintenanceDialog;
